/**
 * Local static data — mirrored from the backend /api/company response so we
 * can render pages immediately while /api/company loads.
 */

export const SERVICES = [
    {
        key: "managed-firewall",
        title: "Managed Firewall",
        icon: "Shield",
        description:
            "Multi-vendor firewall design, deployment, and 24/7 monitoring across Fortinet, WatchGuard, Zyxel, and pfSense. We engineer policy that maps to business intent rather than IP soup — segmenting north-south and east-west traffic, hardening WAN edges, and continuously tuning rules from real telemetry. Our team owns the runbook from greenfield commissioning to lifecycle replacement.",
    },
    {
        key: "it-management",
        title: "IT Management",
        icon: "Server",
        description:
            "Proactive systems administration that keeps fleets healthy before tickets are filed. We cover patch management, asset lifecycle, identity hygiene, backup verification, and cost governance across on-prem, hybrid, and cloud estates. Each engagement is anchored by measurable SLAs and quarterly infrastructure reviews — no black-box managed-services.",
    },
    {
        key: "edr-security",
        title: "EDR Security",
        icon: "Activity",
        description:
            "Endpoint Detection and Response with SOC-grade telemetry, behavioral analytics, and senior-analyst threat hunting. We instrument workstations and servers with tuned EDR agents, stream signals into a curated detection pipeline, and contain incidents in minutes — not hours. Every alert is triaged by a human, every action is auditable.",
    },
    {
        key: "support-ir",
        title: "Support & Incident Response",
        icon: "LifeBuoy",
        description:
            "On-call expert support for the rare moments when things go sideways: breach response, ransomware containment, forensic-grade investigation, and post-incident hardening. We integrate with your existing tooling, preserve chain of custody, and produce executive-ready reporting suitable for regulators, insurers, and your board.",
    },
    {
        key: "zeroconf-ztp",
        title: "ZeroConf ZTP",
        icon: "Cpu",
        description:
            "Our flagship product. Generate enterprise-grade CLI and XML firewall configurations for Fortinet, WatchGuard, Zyxel, and pfSense in minutes, not weeks. A guided wizard turns network intent into vendor-correct configs that engineers can review, diff, and ship — perfect for branch rollouts, MSP fleets, and lab automation. Available to Pro and MSP plan subscribers.",
        highlight: true,
    },
];

export const PRICING = [
    {
        key: "free",
        name: "Explore",
        code: "Free",
        price: 0,
        priceLabel: "$0",
        cadence: "forever",
        tagline: "For curious teams scoping their cyber posture.",
        features: [
            "Concierge AI chat",
            "Public knowledge base & blog",
            "Read-only ZeroConf preview",
        ],
        cta: "Get started",
        popular: false,
    },
    {
        key: "pro",
        name: "Operate",
        code: "Pro",
        price: 49,
        priceLabel: "$49",
        cadence: "per user / month",
        tagline: "Production-grade ZeroConf for in-house security teams.",
        features: [
            "Full ZeroConf ZTP wizard",
            "All 4 vendors (Fortinet, WatchGuard, Zyxel, pfSense)",
            "Save up to 10 projects",
            "Email support · 1 business day",
            "Config history & versioning",
        ],
        cta: "Join waitlist",
        popular: true,
    },
    {
        key: "msp",
        name: "Orchestrate",
        code: "MSP",
        price: 199,
        priceLabel: "$199",
        cadence: "per seat / month",
        tagline: "Multi-tenant orchestration for MSPs and consultancies.",
        features: [
            "Everything in Pro",
            "Multi-tenant workspaces",
            "Unlimited projects & seats",
            "Editable hardware dictionary",
            "Priority support · 4-hour response",
            "White-label option (add-on)",
        ],
        cta: "Talk to sales",
        popular: false,
    },
];

export const VENDORS = ["Fortinet", "WatchGuard", "Zyxel", "pfSense"];

export const METHOD_STEPS = [
    {
        key: "assess",
        title: "Assess",
        icon: "Search",
        body:
            "We start by mapping your real attack surface — assets, identities, data flows, and existing controls. No assumptions, no copy-paste questionnaires.",
    },
    {
        key: "design",
        title: "Design",
        icon: "Compass",
        body:
            "We architect a security strategy that aligns with business objectives, not template diagrams. Every decision is justified, every trade-off documented.",
    },
    {
        key: "deploy",
        title: "Deploy",
        icon: "Rocket",
        body:
            "Implementation with operational rigor: change windows, rollback plans, instrumentation from day one. We ship like a SaaS team, not a consultancy.",
    },
    {
        key: "defend",
        title: "Defend",
        icon: "ShieldCheck",
        body:
            "Continuous monitoring, threat hunting, and posture refinement. Quarterly executive reviews tied to the metrics your board actually cares about.",
    },
];

export const VALUES = [
    {
        key: "trust",
        title: "Trust",
        icon: "Lock",
        body:
            "We treat your environment with the same paranoia we'd apply to our own. Least privilege, audit trails, zero surprises in the invoice or the runbook.",
    },
    {
        key: "innovation",
        title: "Innovation",
        icon: "Sparkles",
        body:
            "Our engineering investment turns repetitive security work into automation — ZeroConf ZTP is one example. We refuse to charge for toil.",
    },
    {
        key: "resilience",
        title: "Resilience",
        icon: "Activity",
        body:
            "Designing for failure is the work. We assume breach, plan recovery, and prove it with quarterly tabletop exercises before incidents ever happen.",
    },
];
